import { useEffect, useState } from 'react'
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react'

export type NotificationType = 'success' | 'error' | 'info' | 'warning'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  duration?: number
}

interface NotificationProps {
  notification: Notification
  onClose: (id: string) => void
}

export function NotificationItem({ notification, onClose }: NotificationProps) {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false)
      setTimeout(() => onClose(notification.id), 300)
    }, notification.duration || 5000)

    return () => clearTimeout(timer)
  }, [notification.id, notification.duration, onClose])

  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    info: Info,
    warning: AlertTriangle,
  }

  const colors = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-orange-50 border-orange-200 text-orange-800',
  }

  const Icon = icons[notification.type]

  return (
    <div
      className={`fixed top-4 right-4 left-4 md:left-auto md:max-w-sm z-50 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
    >
      <div className={`card border-2 ${colors[notification.type]}`}>
        <div className="flex items-start">
          <Icon className="mr-3 mt-0.5 flex-shrink-0" size={20} />
          <div className="flex-1">
            <div className="font-semibold text-sm mb-1">{notification.title}</div>
            <div className="text-sm">{notification.message}</div>
          </div>
          <button
            onClick={() => {
              setIsVisible(false)
              setTimeout(() => onClose(notification.id), 300)
            }}
            className="ml-2 flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}

export function NotificationContainer() {
  const [notifications, setNotifications] = useState<Notification[]>([])

  useEffect(() => {
    // 전역 알림 이벤트 리스너
    const handleNotification = (event: CustomEvent<Notification>) => {
      const notification = {
        ...event.detail,
        id: event.detail.id || Date.now().toString(),
      }
      setNotifications((prev) => [...prev, notification])
    }

    window.addEventListener('notification' as any, handleNotification as EventListener)

    return () => {
      window.removeEventListener('notification' as any, handleNotification as EventListener)
    }
  }, [])

  const handleClose = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  return (
    <div className="fixed top-0 right-0 z-50 pointer-events-none">
      {notifications.map((notification) => (
        <div key={notification.id} className="pointer-events-auto">
          <NotificationItem
            notification={notification}
            onClose={handleClose}
          />
        </div>
      ))}
    </div>
  )
}

export function showNotification(notification: Omit<Notification, 'id'>) {
  const customEvent = new CustomEvent('notification', {
    detail: {
      ...notification,
      id: Date.now().toString(),
    },
  })
  window.dispatchEvent(customEvent)
}

